import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { IForum, IForumLightWeight, IForumPost, IForumPostEdit } from '@fi-shared/fi-forum/fi-forum';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { FiForumPostAddScreenComponent } from '@fi-shared/fi-forum/fi-forum-post-edit/fi-forum-post-add-screen.component';
import { FicScreenService } from '@fi-shared/fic-screen/fic-screen.service';
import { PersonalNotificationsCategoryType } from '@fi-shared/types';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';

@Injectable()
export class FiForumSharedService {
    constructor(
        protected fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private ficScreenService: FicScreenService,
        private stateService: FiStateService,
    ) {}

    getForumsListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IForum>>(
            'Forum/Forum/GetForumsListData',
            parameter,
        );
    }

    getForums(parameterApiModel: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IForum[]>('Forum/Forum/GetForums', parameterApiModel);
    }

    getForumsCount(parameterApiModel: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<number>('Forum/Forum/GetForumsCount', parameterApiModel);
    }

    getLightWeightObjectsForLocalSearch(parameterApiModel: IItemsListParameterBase, v2?: boolean) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            `Forum/Forum/GetLightWeightObjectsForLocalSearch?v2=${!!v2}`,
            parameterApiModel,
        );
    }

    deleteForums(ids: number[]) {
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>('Forum/Forum/DeleteForums', ids));
    }

    markForumPostsAsReadByForumIds(forumIds: number[]) {
        return this.fiBackend.fetchDataPost<number[]>('ForumPost/ForumPost/MarkForumPostsAsReadByForumIds', forumIds);
    }

    markForumPostsAsVisited(forumId: number, forumPostIds: number[]) {
        return this.fiBackend.fetchDataPost<number[]>(
            'ForumPost/ForumPost/MarkForumPostsAsVisited/' + forumId,
            forumPostIds,
        );
    }

    getForumForView(forumId: number) {
        return this.fiBackend.fetchData<IForum>(`Forum/Forum/GetForumForView/${forumId}`);
    }

    getForumsLightWeight() {
        return this.fiBackend.fetchData<IForumLightWeight[]>('Forum/Forum/GetLightWeightForums');
    }

    getForumPostsListData(forumId: number, parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IForumPost>>(
            `ForumPost/ForumPost/GetForumPostsListData?forumId=${forumId}`,
            parameter,
        );
    }

    getForumPosts(forumId: number, parameterApiModel: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IForumPost[]>(
            `ForumPost/ForumPost/GetForumPosts?forumId=${forumId}`,
            parameterApiModel,
        );
    }

    getForumPostsCount(forumId: number, parameterApiModel: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<number>(
            `ForumPost/ForumPost/GetForumPostsCount?forumId=${forumId}`,
            parameterApiModel,
        );
    }

    deleteForumPosts(forumPostIds: number[]): IFiHttpPromise<void> {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost(`ForumPost/ForumPost/DeleteForumPosts`, forumPostIds),
        );
    }

    getForumPost(forumPostId: number) {
        return this.fiBackend.fetchData<IForumPostEdit>(`ForumPost/ForumPost/GetForumPost/${forumPostId}`);
    }

    saveForumPost(forumPost: IForumPostEdit) {
        return this.fiBackend.fetchDataPost<number>(`ForumPost/ForumPost/SaveForumPost`, forumPost);
    }

    getNewForumPostInstance(forumId: number) {
        const forumPost: IForumPostEdit = {
            Title: undefined,
            Content: undefined,
            ForumId: forumId,
            MediaBankFileReferences: [],
            Links: [],
            Reactions: [],
            HasDeleteAccess: true,
            HasEditAccess: true,
            NotificationCategory: PersonalNotificationsCategoryType.Visited,
            ReadsData: [],
            TaggedUsers: [],
        };
        return forumPost;
    }

    createNewForumPost(isMobileView: boolean, forumId: number, forumPostSavedMobileCallback?: () => void): void {
        if (!isMobileView) {
            this.stateService.go(FiStateNames.Forum.Post.Add, { forumId: forumId });
        } else {
            this.openForumPostAddScreen(forumId, () => forumPostSavedMobileCallback);
        }
    }

    openForumPostAddScreen(forumId: number, forumPostSaved: (forumId: number) => void) {
        return this.ficScreenService.openScreen({
            componentType: FiForumPostAddScreenComponent,
            data: {
                forumId: forumId,
                forumPostSaved: (forumId: number) => forumPostSaved(forumId),
            },
        });
    }
}
